import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';

export default function LoginPage() {
  const { login, loginMfa } = useAuth();
  const { t, lang, switchLang } = useLang();
  const navigate = useNavigate();
  const [mode, setMode]         = useState('login'); // 'login' | 'forgot' | 'mfa'
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [mfaToken, setMfaToken] = useState('');
  const [code, setCode]         = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.mfaRequired) { setMfaToken(res.mfaToken); setCode(''); setMode('mfa'); }
      else navigate('/');
    }
    catch { setError(t('login_error')); }
    finally { setLoading(false); }
  }

  async function handleMfa(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await loginMfa(mfaToken, code); navigate('/'); }
    catch (err) { setError(err.response?.data?.error || t('login_error')); }
    finally { setLoading(false); }
  }

  async function handleForgot(e) {
    e.preventDefault();
    setError(''); setLoading(true);
    try { await api.post('/api/auth/forgot-password', { email }); }
    catch { /* neutraal blijven */ }
    finally { setForgotSent(true); setLoading(false); }
  }

  function switchMode(m) {
    setMode(m); setError(''); setForgotSent(false);
  }

  return (
    <div className="login-page">
      <div className="card shadow-sm" style={{ width: 360 }}>
        <div className="card-body p-4">
          <div className="d-flex justify-content-end mb-3">
            <div className="lang-toggle">
              <button className={lang === 'nl' ? 'active' : ''} onClick={() => switchLang('nl')}>NL</button>
              <button className={lang === 'en' ? 'active' : ''} onClick={() => switchLang('en')}>EN</button>
            </div>
          </div>
          <div className="text-center mb-4">
            <img src="/logo-fdconsult.png" alt="FDConsult" style={{ height: 56, maxWidth: '100%' }} />
            <div className="text-muted small mt-2 fw-medium" style={{ letterSpacing: '0.05em' }}>Audit Tool</div>
          </div>

          {error && <div className="alert alert-danger py-2">{error}</div>}

          {mode === 'login' && (
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label small fw-medium">{t('login_email')}</label>
                <input type="email" className="form-control" value={email}
                  onChange={e => setEmail(e.target.value)} autoFocus required />
              </div>
              <div className="mb-3">
                <label className="form-label small fw-medium">{t('login_password')}</label>
                <input type="password" className="form-control" value={password}
                  onChange={e => setPassword(e.target.value)} required />
              </div>
              <button type="submit" className="btn btn-primary w-100 mt-1" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {t('login_submit')}
              </button>
              <div className="text-center mt-3">
                <button type="button" className="btn btn-link btn-sm p-0 text-muted"
                  onClick={() => switchMode('forgot')}>
                  {t('login_forgot')}
                </button>
              </div>
            </form>
          )}

          {mode === 'forgot' && (
            forgotSent ? (
              <div>
                <div className="alert alert-success py-2">{t('forgot_sent')}</div>
                <button type="button" className="btn btn-link btn-sm p-0 text-muted"
                  onClick={() => switchMode('login')}>
                  ← {t('forgot_back')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgot}>
                <div className="text-muted small mb-3">{t('forgot_intro')}</div>
                <div className="mb-3">
                  <label className="form-label small fw-medium">{t('login_email')}</label>
                  <input type="email" className="form-control" value={email}
                    onChange={e => setEmail(e.target.value)} autoFocus required />
                </div>
                <button type="submit" className="btn btn-primary w-100 mt-1" disabled={loading}>
                  {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                  {t('forgot_submit')}
                </button>
                <div className="text-center mt-3">
                  <button type="button" className="btn btn-link btn-sm p-0 text-muted"
                    onClick={() => switchMode('login')}>
                    ← {t('forgot_back')}
                  </button>
                </div>
              </form>
            )
          )}

          {mode === 'mfa' && (
            <form onSubmit={handleMfa}>
              <div className="text-muted small mb-3">{t('mfa_login_intro')}</div>
              <div className="mb-3">
                <label className="form-label small fw-medium">{t('mfa_code')}</label>
                <input type="text" inputMode="numeric" autoComplete="one-time-code"
                  className="form-control text-center" style={{ letterSpacing: '0.3em', fontSize: 20 }}
                  value={code} onChange={e => setCode(e.target.value)}
                  maxLength={6} autoFocus required />
              </div>
              <button type="submit" className="btn btn-primary w-100 mt-1" disabled={loading}>
                {loading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
                {t('mfa_verify')}
              </button>
              <div className="text-center mt-3">
                <button type="button" className="btn btn-link btn-sm p-0 text-muted"
                  onClick={() => switchMode('login')}>
                  ← {t('forgot_back')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
